// frontend/app/components/lessonPlans/authoring/blockFields.js
//
// Field kinds, the fields several block types share, and the evidence weights
// the mastery engine applies to what a student writes.
//
// Split out of blockSchemas.js so the core table and the Loop table can both
// reach these without importing each other.

/** Field kinds the generic editor knows how to render. */
export const FIELD = {
  TEXT: "text",
  LONG_TEXT: "long_text",
  NUMBER: "number",
  TOGGLE: "toggle",
  STRING_LIST: "string_list",
  PROMPT_LIST: "prompt_list", // [{key, label}] — the reflection prompts
  CHIPS: "chips",
  CHOICE: "choice", // single-select among fixed options, e.g. asset tier
  OPTIONS: "options", // [{id, text}] with a single correct answer
  MULTI_OPTIONS: "multi_options", // [{id, text}] with several correct answers
  ORDER_ITEMS: "order_items", // [{id, text}] whose authored order is the answer
  CONCEPT: "concept",
  PWO_REF: "pwo_ref",
  VIDEO_REF: "video_ref",
  TOPIC_QUIZ_REF: "topic_quiz_ref",
};

/**
 * How much one answer weighs as evidence of mastery, straight from the design
 * doc's evidence table. `m = (Σ weight×credit + κ·m₀) / (Σ weight + κ)`, so a
 * block with weight 0 is recorded and shown to the teacher but never moves a
 * band — and a lesson whose only tagged blocks are weight 0 can never produce
 * a mastery score at all. Authors have to be able to see that before they
 * publish, which is why the number is on the palette and on every block.
 */
export const EVIDENCE_WEIGHT = {
  EXPLANATION: 1.5,
  GRADED: 1.0,
  PROBE: 0.5,
  NONE: 0,
};

/** Author-facing wording for each weight. Highest first. */
export const EVIDENCE_LEVELS = [
  {
    weight: EVIDENCE_WEIGHT.EXPLANATION,
    label: "Strongest evidence",
    caption: "Counts 1.5× — articulating an idea proves more than picking an option",
  },
  {
    weight: EVIDENCE_WEIGHT.GRADED,
    label: "Counts in full",
    caption: "Counts 1× towards mastery",
  },
  {
    weight: EVIDENCE_WEIGHT.PROBE,
    label: "Counts a little",
    caption: "Counts 0.5× — a first look, not a checked answer",
  },
  {
    weight: EVIDENCE_WEIGHT.NONE,
    label: "Never scored",
    caption: "Recorded for the teacher, but it cannot move a mastery band",
  },
];

/** The wording for `weight`, or null for a block that records no answer. */
export const evidenceLevelFor = (weight) =>
  weight === null || weight === undefined
    ? null
    : EVIDENCE_LEVELS.find((level) => level.weight === weight) || null;

/**
 * Palette groups, ordered by what the author is reaching for: things to read,
 * Saras content to open, answers to grade, thinking to write, and the closing
 * look back. A `reflection` sitting next to a `mastery_map` rather than next
 * to a multiple choice is the whole point — it stops one landing in Wonder.
 */
export const BLOCK_GROUPS = [
  { key: "content", label: "Content" },
  { key: "asset", label: "Saras content" },
  { key: "check", label: "Checks" },
  { key: "written", label: "Thinking in writing" },
  { key: "growth", label: "Reflect and grow" },
];

export const titleField = {
  key: "title",
  label: "Heading",
  kind: FIELD.TEXT,
  placeholder: "Optional heading",
};

export const instructionsField = {
  key: "instructions",
  label: "What to do with it",
  kind: FIELD.LONG_TEXT,
  placeholder: "Rotate the model and ask students to point out the key parts.",
};

/**
 * Whether a Saras asset is one the teacher is expected to use in class, or
 * optional if there is time (S2, the SME review's "CORE/EXTENDED
 * INSTRUCTIONAL ASSETS" headings). Shared by every launchable Saras asset
 * type — never `image`, which is not a launchable classroom asset.
 *
 * `default` rather than `defaultValue` on purpose: `makeBlock` only
 * auto-writes a field that declares `defaultValue`, and a freshly authored
 * block should stay the same "absent" shape `normalize_asset_tier` already
 * reads as `core` on the backend. `default` only feeds which chip the editor
 * shows as selected before the author has touched it.
 */
export const assetTierField = {
  key: "asset_tier",
  label: "Use in class",
  kind: FIELD.CHOICE,
  options: [
    { value: "core", label: "Core — must use" },
    { value: "extended", label: "Extended — optional" },
  ],
  default: "core",
  hint: "Core assets are grouped first in the lesson and marked as expected.",
};

export const explanationField = {
  key: "explanation",
  label: "Explanation shown after answering",
  kind: FIELD.LONG_TEXT,
  placeholder: "State the key idea in one clear sentence.",
};

export const conceptField = {
  key: "concept_key",
  label: "Concept",
  kind: FIELD.CONCEPT,
  hint: "Untagged checks are invisible to Teacher Insights.",
};

export const placeholderField = {
  key: "placeholder",
  label: "Hint text in the box",
  kind: FIELD.TEXT,
};

export const aiFeedbackField = {
  key: "ai_feedback",
  label: "Saras responds to the answer",
  kind: FIELD.TOGGLE,
  defaultValue: true,
  hint: "Falls back to a written-in prompt if the AI is unavailable.",
};
